import { useEffect, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import { WORKSPACE_DETAILS_FORM_FIELDS } from '../../shared/fieldNames';
import { WorkspaceDetailsDependencyContext } from '../../shared/types';

/**
 * Reset the business-number-dependent fields when the country changes.
 * Mirrors the legacy reset effect at
 * packages/frontend/src/components/WorkspaceDetailsForm.tsx:282-298
 * (reset on dirtyFields.businessNumberCountry → blank business fields, recompute canProceed).
 * The reset is skipped on mount so prefilled values survive the first render.
 */
export const useBusinessNumberCountryDependencies = ({
  form,
}: WorkspaceDetailsDependencyContext) => {
  const { control, formState, setValue } = form;
  const { isDirty } = formState;
  const businessNumberCountry = useWatch({
    control,
    name: WORKSPACE_DETAILS_FORM_FIELDS.businessNumberCountry,
  });
  const previous = useRef(businessNumberCountry);

  useEffect(() => {
    if (previous.current === businessNumberCountry) return;
    previous.current = businessNumberCountry;

    if (!isDirty) return;

    setValue(WORKSPACE_DETAILS_FORM_FIELDS.businessNumber, '', {
      shouldDirty: true,
    });
    setValue(WORKSPACE_DETAILS_FORM_FIELDS.businessNumberVerified, false, {
      shouldDirty: true,
    });
    setValue(WORKSPACE_DETAILS_FORM_FIELDS.entityName, '', {
      shouldDirty: true,
    });
    setValue(WORKSPACE_DETAILS_FORM_FIELDS.abnStatus, null);
    setValue(WORKSPACE_DETAILS_FORM_FIELDS.gstStatus, null);
    setValue(
      WORKSPACE_DETAILS_FORM_FIELDS.canProceed,
      businessNumberCountry !== 'AU',
      { shouldValidate: true },
    );
  }, [businessNumberCountry, isDirty, setValue]);

  return businessNumberCountry;
};
